import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

/**
 * TokenDisplay - Shows token balance in a compact format for the navbar
 * Refreshes on every route change to show up-to-date token usage
 */
const TokenDisplay = ({ auth }) => {
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Refetch token data on route changes
  useEffect(() => {
    if (auth && auth.id) {
      fetchTokenData();
    }
  }, [auth, location.pathname]);

  const fetchTokenData = async () => {
    try {
      const response = await fetch('/api/user/tenants', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const activeTenant = data.tenants?.find(t => t.is_active) || data.tenants?.[0];
        if (activeTenant) {
          setTokenData({
            balance: activeTenant.token_balance || 0,
            allocation: activeTenant.monthly_token_allocation || 0,
            spent: activeTenant.monthly_tokens_used || 0
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch token data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Don't render if not authenticated or still loading
  if (!auth || !auth.id || loading || !tokenData) {
    return null;
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toLocaleString();
  };

  // Total tokens available for the period = current remaining + used this month.
  // This is invariant across the month (monthly allocation + one-off bonus) because every
  // charge lowers the balance and raises "used" by the same amount. Using it as the meter
  // denominator makes the navbar match the tooltip's "X tokens available" (incl. bonus),
  // instead of showing only the monthly allocation.
  const totalAvailable = (tokenData.balance || 0) + (tokenData.spent || 0);

  // Color based on remaining balance vs total available for the period
  const getColor = () => {
    if (tokenData.balance <= 0) return '#dc2626'; // red - no tokens left
    if (tokenData.balance < totalAvailable * 0.1) return '#dc2626'; // red - below 10% remaining
    if (tokenData.balance < totalAvailable * 0.25) return '#f59e0b'; // amber - below 25% remaining
    return '#10b981'; // green - healthy balance
  };

  const hasBonus = tokenData.balance > tokenData.allocation;

  const tooltipContent = (
    <Tooltip id="token-tooltip" style={{ maxWidth: '280px' }} className="token-tooltip-opaque">
      <div style={{ textAlign: 'left', padding: '0.25rem' }}>
        <div style={{ fontWeight: '600', marginBottom: '0.35rem' }}>Token Usage</div>
        <div style={{ marginBottom: '0.15rem' }}>
          <strong>{formatNumber(tokenData.spent)}</strong> tokens used this month
        </div>
        <div style={{ marginBottom: '0.15rem' }}>
          <strong>{formatNumber(tokenData.balance)}</strong> tokens available
        </div>
        <div style={{ marginBottom: '0.35rem' }}>
          Monthly allocation: <strong>{formatNumber(tokenData.allocation)}</strong>
          {hasBonus && <span style={{ color: '#10b981' }}> + bonus</span>}
        </div>
        <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
          Monthly allocation resets each month. Bonus tokens do not reset.
        </div>
      </div>
    </Tooltip>
  );

  return (
    <OverlayTrigger placement="bottom" overlay={tooltipContent}>
      <div
        className="d-flex align-items-center"
        style={{
          fontSize: '0.8rem',
          color: '#64748b',
          cursor: 'help'
        }}
      >
        <span style={{ color: '#94a3b8' }}>
          {formatNumber(tokenData.spent)}
        </span>
        <span style={{ color: '#94a3b8', marginLeft: '0.2rem', marginRight: '0.2rem' }}>
          /
        </span>
        <span style={{ color: getColor(), fontWeight: '500' }}>
          {formatNumber(totalAvailable)}
        </span>
      </div>
    </OverlayTrigger>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(TokenDisplay);
